
import { Megaphone, AlertTriangle, Info, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: string;
  createdAt: string | number;
  createdByEmail: string | null;
}

interface AnnouncementsBannerProps {
  announcements: Announcement[];
  isLoading: boolean;
  messagesLink: string;
  variant?: 'parent' | 'coach';
}

const priorityStyles: Record<string, { bg: string; border: string; icon: string }> = {
  urgent: { bg: 'bg-red-50', border: 'border-l-red-500', icon: 'text-red-500' },
  high: { bg: 'bg-amber-50', border: 'border-l-amber-500', icon: 'text-amber-500' },
  normal: { bg: 'bg-blue-50', border: 'border-l-blue-500', icon: 'text-blue-500' },
  low: { bg: 'bg-gray-50', border: 'border-l-gray-400', icon: 'text-gray-400' },
};

function formatTimeAgo(dateStr: string | number): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function AnnouncementsBanner({
  announcements,
  isLoading,
  messagesLink,
  variant = 'parent',
}: AnnouncementsBannerProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!announcements || announcements.length === 0) {
    return null; // Don't show the card if there are no announcements
  }

  const variantColors = variant === 'parent'
    ? 'text-green-600'
    : 'text-blue-600';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Megaphone className={cn('w-5 h-5', variantColors)} />
            Announcements
          </CardTitle>
          <Link
            to={messagesLink}
            className={cn('text-sm flex items-center gap-1 hover:underline', variantColors)}
          >
            View all
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {announcements.slice(0, 3).map((announcement) => {
          const style = priorityStyles[announcement.priority] || priorityStyles.normal;
          const IconComponent = announcement.priority === 'urgent' || announcement.priority === 'high'
            ? AlertTriangle
            : Info;

          return (
            <div
              key={announcement.id}
              className={cn(
                'p-3 rounded-lg border-l-4 transition-colors',
                style.bg,
                style.border
              )}
            >
              <div className="flex items-start gap-3">
                <IconComponent className={cn('w-5 h-5 mt-0.5 flex-shrink-0', style.icon)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold text-gray-900 truncate">
                      {announcement.title}
                    </h4>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {formatTimeAgo(announcement.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {announcement.content}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
